import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import ComingSoon from "@/shared/components/ComingSoon";
import type { MetaIcon } from "../../../shared/constants/icon.constant";
import PrimaryIcon from "@/shared/components/ui/PrimaryIcon";

export interface SidebarItem {
  label?: string;
  path?: string;
  icon?: MetaIcon;
  children?: SidebarItem[];
  comingSoon?: boolean;
  hideChildren?: boolean;
  sidebarPath?: string;
}

interface SidebarProps {
  items: SidebarItem[];
}

export default function Sidebar({ items }: Readonly<SidebarProps>) {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [showComingSoon, setShowComingSoon] = useState(false);

  const handleComingSoonClick = () => {
    setShowComingSoon(true);
  };

  return (
    <aside className="h-full md:w-60 w-full flex flex-col bg-sidebar">
      <nav className="flex-1 px-3 sm:px-4 py-6 pr-0! overflow-y-auto border-r">
        <ul className="space-y-1">
          {items
            .filter((item) => item.icon && item.label)
            .map((item) => (
              <SidebarNode
                key={item.label}
                item={item}
                parentPath=""
                openMenu={openMenu}
                setOpenMenu={setOpenMenu}
                onComingSoonClick={handleComingSoonClick}
              />
            ))}
        </ul>
      </nav>
      {showComingSoon && (
        <ComingSoon onClose={() => setShowComingSoon(false)} />
      )}
    </aside>
  );
}

interface SidebarNodeProps {
  item: SidebarItem;
  parentPath: string;
  openMenu: string | null;
  setOpenMenu: (label: string | null) => void;
  onComingSoonClick: () => void;
}

function SidebarNode({
  item,
  parentPath,
  openMenu,
  setOpenMenu,
  onComingSoonClick,
}: Readonly<SidebarNodeProps>) {
  const location = useLocation();
  const hasChildren =
    !item.hideChildren && item.children && item.children.length > 0;

  const fullPath =
    parentPath && item.path
      ? `${parentPath.replace(/\/$/, "")}/${item.path.replace(/^\//, "")}`
      : item.path || parentPath;

  const linkPath = item.sidebarPath
    ? parentPath
      ? `${parentPath.replace(/\/$/, "")}/${item.sidebarPath.replace(/^\//, "")}`
      : item.sidebarPath
    : fullPath;

  const normalizedLinkPath = linkPath ? `/${linkPath}` : null;
  const isActive =
    normalizedLinkPath === location.pathname ||
    `/${fullPath}` === location.pathname;

  const hasActiveChild =
    hasChildren &&
    item.children!.some((child) => {
      const childFullPath =
        fullPath && child.path
          ? `${fullPath.replace(/\/$/, "")}/${child.path.replace(/^\//, "")}`
          : child.path || fullPath;
      return `/${childFullPath}` === location.pathname;
    });

  const isActiveOrChild = isActive || hasActiveChild;

  const open = openMenu === item.label;

  const handleClick = () => {
    if (hasChildren) {
      setOpenMenu(open ? null : item.label!);
    }
  };

  useEffect(() => {
    if (hasActiveChild) {
      setOpenMenu(item.label!);
    }
  }, [hasActiveChild, item.label, setOpenMenu]);

  const baseItemClass = `flex items-center justify-between w-full px-3 py-1.5 text-sm cursor-pointer gap-2 transition ${
    parentPath
      ? "border-l-2 border-sidebar-active ml-3.5 -mt-1 rounded-r-md rounded-l-none"
      : "rounded-md"
  }`;
  const stateClass = isActiveOrChild
    ? "text-[#E3B33F] hover:bg-white/5"
    : "hover:bg-white/5 text-white hover:text-label-dark";
  const itemClass = `${baseItemClass} ${stateClass}`;

  return (
    <li className="text-white mr-4">
      {hasChildren ? (
        <button type="button" className={itemClass} onClick={handleClick}>
          <div>
            <PrimaryIcon
              isActive={isActiveOrChild ?? false}
              icon={item.icon!}
            />
          </div>
          <span className="flex-1 text-left">{item.label}</span>
        </button>
      ) : (
        linkPath && (
          <Link
            to={linkPath}
            className={itemClass}
            onClick={(event) => {
              if (item.comingSoon) {
                event.preventDefault();
                onComingSoonClick();
              }
            }}>
            <div>
              <PrimaryIcon
                isActive={isActiveOrChild ?? false}
                icon={item.icon!}
              />
            </div>
            <span className="flex-1 text-left">{item.label}</span>
          </Link>
        )
      )}

      {hasChildren && open && (
        <ul className="ml-1 mt-1 space-y-1">
          {item.children!.map((child) => (
            <SidebarNode
              key={child.label}
              item={child}
              parentPath={fullPath || ""}
              openMenu={openMenu}
              setOpenMenu={setOpenMenu}
              onComingSoonClick={onComingSoonClick}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
